/**
 * Country settings for currency, tax, and VAT/sales tax
 */

export const COUNTRIES = [
  // Supported (English-speaking) countries
  {
    id: 'ie',
    name: 'Ireland',
    currency: 'EUR',
    currencySymbol: '€',
    taxSystem: 'vat',
    vatRates: [
      { id: '23', rate: 23, name: 'Standard Rate' },
      { id: '13.5', rate: 13.5, name: 'Reduced Rate' },
      { id: '9', rate: 9, name: 'Second Reduced Rate' },
      { id: '0', rate: 0, name: 'Zero Rate' }
    ],
    defaultVatRate: 23,
    taxYear: { start: { month: 0, day: 1 }, end: { month: 11, day: 31 } }, // Calendar year
    incomeTaxInfo: 'Progressive tax rates with standard rate of 20% and higher rate of 40%.',
    terminology: {
      taxName: 'VAT',
      taxFullName: 'Value Added Tax (VAT)',
      taxRegistrationName: 'VAT registration',
      businessTypes: {
        sole_trader: 'Sole Trader / Self-Employed',
        limited_company: 'Limited Company'
      },
      taxAuthority: 'Revenue'
    }
  },
  {
    id: 'uk',
    name: 'United Kingdom',
    currency: 'GBP',
    currencySymbol: '£',
    taxSystem: 'vat',
    vatRates: [
      { id: '20', rate: 20, name: 'Standard Rate' },
      { id: '5', rate: 5, name: 'Reduced Rate' },
      { id: '0', rate: 0, name: 'Zero Rate' }
    ],
    defaultVatRate: 20,
    taxYear: { start: { month: 3, day: 6 }, end: { month: 3, day: 5 } }, // April 6 to April 5
    incomeTaxInfo: 'Progressive tax rates with basic rate of 20%, higher rate of 40%, and additional rate of 45%.',
    terminology: {
      taxName: 'VAT',
      taxFullName: 'Value Added Tax (VAT)',
      taxRegistrationName: 'VAT registration',
      businessTypes: {
        sole_trader: 'Sole Trader / Self-Employed',
        limited_company: 'Limited Company / LTD'
      },
      taxAuthority: 'HMRC'
    }
  },
  {
    id: 'us',
    name: 'United States',
    currency: 'USD',
    currencySymbol: '$',
    taxSystem: 'sales',
    salesTaxRates: [
      { id: '0', rate: 0, name: 'No Tax' },
      { id: '4', rate: 4, name: 'Low (4%)' },
      { id: '6', rate: 6, name: 'Medium (6%)' },
      { id: '8', rate: 8, name: 'High (8%)' },
      { id: '10', rate: 10, name: 'Very High (10%)' }
    ],
    defaultSalesTax: 6, // Average sales tax
    taxYear: { start: { month: 0, day: 1 }, end: { month: 11, day: 31 } }, // Calendar year
    incomeTaxInfo: 'Federal progressive tax rates from 10% to 37% depending on income brackets. Additional state income taxes may apply.',
    terminology: {
      taxName: 'Sales Tax',
      taxFullName: 'Sales Tax',
      taxRegistrationName: 'sales tax registration',
      businessTypes: {
        sole_trader: 'Sole Proprietor / Self-Employed',
        limited_company: 'LLC / Corporation'
      },
      taxAuthority: 'IRS/state tax authority'
    }
  }
];

const COUNTRY_ID_ALIASES = {
  ie: 'ie',
  ireland: 'ie',
  uk: 'uk',
  gb: 'uk',
  gbp: 'uk',
  unitedkingdom: 'uk',
  us: 'us',
  usa: 'us',
  usd: 'us',
  unitedstates: 'us',
  unitedstatesofamerica: 'us'
};

const sanitizeCountryId = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z]/g, '');

export const normalizeCountryId = (value) => {
  const sanitized = sanitizeCountryId(value);
  if (!sanitized) return 'ie';
  return COUNTRY_ID_ALIASES[sanitized] || sanitized;
};

/**
 * Get a country by its ID
 * @param {string} id - The country ID
 * @returns {Object} The country object
 */
export const getCountryById = (id) => {
  const normalizedId = normalizeCountryId(id);
  const countryById = COUNTRIES.find((country) => country.id === normalizedId);
  if (countryById) return countryById;

  const normalizedName = String(id || '').trim().toLowerCase();
  const countryByName = COUNTRIES.find((country) => country.name.toLowerCase() === normalizedName);
  if (countryByName) return countryByName;

  return COUNTRIES.find((country) => country.id === 'ie');
};

export const getCountryTerminology = (countryInput) => {
  const country = getCountryById(countryInput);
  const fallbackTaxName = country.taxSystem === 'sales' ? 'Sales Tax' : 'VAT';
  const fallbackBusinessTypes = country.id === 'us'
    ? { sole_trader: 'Sole Proprietor / Self-Employed', limited_company: 'LLC / Corporation' }
    : { sole_trader: 'Sole Trader / Self-Employed', limited_company: 'Limited Company' };

  return {
    taxName: fallbackTaxName,
    taxFullName: country.taxSystem === 'sales' ? 'Sales Tax' : 'Value Added Tax (VAT)',
    taxRegistrationName: `${fallbackTaxName} registration`,
    taxAuthority: country.id === 'uk' ? 'HMRC' : country.id === 'us' ? 'IRS/state tax authority' : 'Revenue',
    ...(country.terminology || {}),
    businessTypes: {
      ...fallbackBusinessTypes,
      ...(country.terminology?.businessTypes || {})
    }
  };
};

export const getBusinessTypeLabelForCountry = (businessType, countryInput) => {
  const normalizedBusinessType = String(businessType || '').trim() === 'limited_company'
    ? 'limited_company'
    : 'sole_trader';
  return getCountryTerminology(countryInput).businessTypes[normalizedBusinessType];
};

/**
 * Get the default VAT/sales tax rate for a country
 * @param {string} countryId - The country ID
 * @returns {number} The default tax rate
 */
export const getDefaultTaxRate = (countryId) => {
  const country = getCountryById(countryId);
  if (country.taxSystem === 'vat') {
    return country.defaultVatRate;
  }
  return country.defaultSalesTax || 0;
};

/**
 * Get all available VAT/sales tax rates for a country
 * @param {string} countryId - The country ID
 * @returns {Array} Array of tax rates
 */
export const getTaxRates = (countryId) => {
  const country = getCountryById(countryId);
  if (country.taxSystem === 'vat') {
    return country.vatRates;
  }
  return country.salesTaxRates || [];
};
